import { supabase } from './supabase'
import { secureRPCWithSchema } from './sqlInjectionProtection'

/**
 * Check if organization has access to a feature
 */
export const hasFeatureAccess = async (organizationId, featureCode) => {
  try {
    const data = await secureRPCWithSchema('has_feature_access', {
      org_id: organizationId,
      feature_code: featureCode,
    })
    return data || false
  } catch (error) {
    console.error('Error checking feature access:', error)
    return false
  }
}

/**
 * Get all available features for organization
 */
export const getOrganizationFeatures = async (organizationId) => {
  try {
    const { data, error } = await supabase.rpc('get_organization_features', {
      org_id: organizationId,
    })

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching organization features:', error)
    return []
  }
}

/**
 * Get organization subscription details
 */
export const getOrganizationSubscription = async (organizationId) => {
  try {
    const { data, error } = await supabase
      .from('organization_subscriptions')
      .select('*, tier:subscription_tiers(*)')
      .eq('organization_id', organizationId)
      .single()

    // Handle 406 (table not accessible) gracefully
    if (error) {
      if (error.status === 406 || error.code === 'PGRST100') {
        console.warn('Organization subscriptions table not accessible')
        return null
      }
      if (error.code !== 'PGRST116') {
        throw error
      }
    }
    return data || null
  } catch (error) {
    console.error('Error fetching subscription:', error)
    return null
  }
}

/**
 * Get all subscription tiers
 */
export const getSubscriptionTiers = async () => {
  try {
    const { data, error } = await supabase
      .from('subscription_tiers')
      .select('*')
      .eq('is_active', true)
      .order('price_monthly', { ascending: true })

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching subscription tiers:', error)
    return []
  }
}

/**
 * Get tier features
 */
export const getTierFeatures = async (tierId) => {
  try {
    const { data, error } = await supabase
      .from('tier_features')
      .select('*, feature:features(*)')
      .eq('tier_id', tierId)
      .eq('is_included', true)

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching tier features:', error)
    return []
  }
}

/**
 * Get all features
 */
export const getAllFeatures = async () => {
  try {
    const { data, error } = await supabase
      .from('features')
      .select('*')
      .order('category', { ascending: true })
      .order('name', { ascending: true })

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching features:', error)
    return []
  }
}

/**
 * Update tier features (master admin only)
 */
export const updateTierFeatures = async (tierId, featureIds, isIncluded = true) => {
  try {
    // Delete existing mappings
    await supabase.from('tier_features').delete().eq('tier_id', tierId)

    // Insert new mappings
    const mappings = featureIds.map((featureId) => ({
      tier_id: tierId,
      feature_id: featureId,
      is_included: isIncluded,
    }))

    const { data, error } = await supabase
      .from('tier_features')
      .insert(mappings)
      .select()

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error updating tier features:', error)
    throw error
  }
}

/**
 * Create or update subscription tier
 */
export const upsertSubscriptionTier = async (tierData) => {
  try {
    const { data, error } = await supabase
      .from('subscription_tiers')
      .upsert(tierData, { onConflict: 'code' })
      .select()
      .single()

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error upserting subscription tier:', error)
    throw error
  }
}

/**
 * Create or update feature
 */
export const upsertFeature = async (featureData) => {
  try {
    const { data, error } = await supabase
      .from('features')
      .upsert(featureData, { onConflict: 'code' })
      .select()
      .single()

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error upserting feature:', error)
    throw error
  }
}

